import React from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import TemplateLibrarySelect from '../../../../fragments/TemplateLibrarySelect';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import CampaignLanguageDropdown, { LanguageStepType } from './CampaignLanguageDropdown';
import { campaignMobile } from '../../utils/campaignBreakpoints';

/**
 * The row under every compose field: Generate · Use Template … Language.
 *
 * One component rather than a copy per step type — the row is the part of the
 * layout the eye uses to recognise "this is where a message is written", so the
 * four step types have to agree on it down to the spacing.
 */

interface Props {
  onGenerate: () => void;
  generateLabel?: string;
  /** Generating needs something to work from — an instruction or a draft. */
  generateDisabled?: boolean;
  onInsertTemplate?: (content: string) => void;
}

/**
 * Undo, for the editor toolbars.
 *
 * Exported as an icon rather than a button: it belongs beside bold, lists and clear —
 * the other things that act on the text — and each toolbar styles its own buttons.
 * It sat in this row for a while, next to Use Template, on the reasoning that this is
 * where the overwriting happens. Wrong instinct: a row of actions that DO something is
 * not where you look for the one that takes something back.
 */
export const UndoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M3 8h11a6 6 0 010 12H8" />
    <path d="M3 8l4-4M3 8l4 4" />
  </svg>
);

const CampaignComposeActions: React.FC<Props> = ({
  onGenerate, generateLabel = 'Generate message', generateDisabled, onInsertTemplate,
}) => (
  <Row>
    <Left>
      <GenerateBtn type="button" onClick={onGenerate} disabled={generateDisabled}>
        {/* Two stars, not one: the single filled diamond read as a generic bullet at
            this size. The small one carries the "AI" association. */}
        <Spark aria-hidden>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
            <path d="M14.2 4.2l1.7 4.6 4.6 1.7-4.6 1.7-1.7 4.6-1.7-4.6L7.9 10.5l4.6-1.7z" />
            <path d="M6.4 14.4l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
          </svg>
        </Spark>
        {generateLabel}
      </GenerateBtn>

      {onInsertTemplate && (
        <TemplateLibrarySelect variant="button" onInsert={onInsertTemplate} />
      )}
    </Left>
  </Row>
);

export default CampaignComposeActions;

/**
 * Language and Preview, right-aligned at the top of a step.
 *
 * Separate from the compose actions below the field because they answer different
 * questions: these two are about the step as a whole ("what language, and what does
 * it look like"), while Generate and Use Template act on the field under them.
 */
export const CampaignStepTools: React.FC<{
  language: string;
  onLanguageChange: (lang: string) => void;
  languageStepType: LanguageStepType;
  onPreview: () => void;
  /** Nothing written yet — there is no preview to show. */
  previewDisabled?: boolean;
  /**
   * Opens sending-account settings over the campaign. Only the email step passes it —
   * a LinkedIn step has no mailbox, signature or sending limit to configure.
   */
  onOpenSettings?: () => void;
}> = ({ language, onLanguageChange, languageStepType, onPreview, previewDisabled, onOpenSettings }) => (
  <ToolsRow>
    <CampaignLanguageDropdown
      stepType={languageStepType}
      value={language}
      onChange={onLanguageChange}
    />
    <PreviewBtn type="button" onClick={onPreview} disabled={previewDisabled}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
        <path d="M1.5 12s4-7 10.5-7 10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      Preview
    </PreviewBtn>
    {onOpenSettings && (
      <TooltipWrapper tooltipText="Mailbox, signature and sending limit" placement="top">
        <IconBtn type="button" onClick={onOpenSettings} aria-label="Sending account settings">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 3.6 1.65 1.65 0 0010 2.09V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 8v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </svg>
        </IconBtn>
      </TooltipWrapper>
    )}
  </ToolsRow>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Row = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  ${campaignMobile`
    flex-direction: column;
    align-items: stretch;
  `}
`;

const ToolsRow = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  flex-wrap: wrap;
  ${campaignMobile`
    justify-content: stretch;
    & > * { flex: 1 1 auto; }
  `}
`;

const IconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; cursor: pointer;
  border: 1.5px solid #e2e8f0; background: #fff; color: #475569;
  transition: all 0.12s;
  &:hover:not(:disabled) { border-color: ${ACCENT}; color: ${ACCENT}; background: #f8f9ff; }
  &:disabled { opacity: .45; cursor: default; }
`;

const PreviewBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  height: 30px; padding: 0 11px; border-radius: 8px; cursor: pointer;
  border: 1.5px solid #e2e8f0; background: #fff; color: #475569;
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  white-space: nowrap;
  transition: all 0.12s;
  &:hover:not(:disabled) { border-color: ${ACCENT}; color: ${ACCENT}; background: #f8f9ff; }
  &:disabled { opacity: .5; cursor: default; }
`;

const Left = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  ${campaignMobile`
    & > * { flex: 1 1 auto; }
  `}
`;

const GenerateBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  height: 32px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  border: 1.5px solid ${ACCENT};
  background: #fff; color: ${ACCENT};
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  white-space: nowrap;
  transition: background 0.12s;
  &:hover:not(:disabled) { background: ${styles.colors.primary100}; }
  &:disabled { opacity: .5; cursor: default; }
`;

const Spark = styled.span`
  display: flex; align-items: center; flex-shrink: 0;
  svg { display: block; }
`;
